<template>
  <div
    :class="{
      'no-wrap': dense && tiny,
      'q-col-gutter-sm': dense && tiny,
      'q-col-gutter-md': !dense || !tiny
    }"
    class="row"
  >
    <target-data
      v-for="target in sortedTargets"
      ref="targets"
      :key="target.id"
      :session-program="sessionProgram"
      :target="target"
      :data="data"
      :active-prompt-levels="activePromptLevels"
      :dense="dense"
      :tiny="tiny"
      :disable="disable"
      :workflow-statuses="workflowStatuses"
      @record="onRecordData"
    />
  </div>
</template>
<script>

import TargetData from 'components/Programs/DataCollection/TargetData';
import {
  chain, find, findIndex, get, head, includes,
  isEmpty, map,
} from 'lodash';
import DataCollectionProgram from 'src/mixins/DataCollectionProgram';

export default {

  name: 'TaskAnalysisProgram',

  components: { TargetData },

  mixins: [DataCollectionProgram],

  props: {
    cachedCurrentProgramTrial: {
      type: Number,
      default: 0,
    },
  },

  emits: ['record-multiple-data', 'record'],

  data() {
    return {
      /**
       * Cached data ensures the right calculation of the trials on collecting targets with default observation and
       * through multiple observation window.
       */
      cache: {
        currentTrialNumber: 0,
        lastTargetTrialNumber: null,
        lastTaskIndex: -1,
      },
    };
  },

  computed: {

    programPromptLevelTemplate() {
      return find(this.activePromptLevels, { id: this.sessionProgram.program.promptLevelTemplate });
    }, // programPromptLevelTemplate()

    programPrompts() {
      return get(this.programPromptLevelTemplate, 'promptLevels');
    }, // programPrompts()

    defaultPromptLevel() {
      return find(this.programPrompts, { id: get(this.sessionProgram, ['program', 'defaultPromptLevel'], '') });
    }, // defaultPromptLevel()

    nextTrialNumber() {
      return this.cache.currentTrialNumber + 1;
    }, // nextTrialNumber()

    currentTrialNumber() {
      return this.cache.lastTargetTrialNumber || this.nextTrialNumber;
    }, // currentTrialNumber()

    /** @type {Object | null} */
    lastTrial() {
      const sortedTargetIds = map(this.sortedTargets, 'id');
      return chain(this.data)
        .get('data')
        .filter((dataItem) => includes(sortedTargetIds, dataItem.target))
        .last()
        .defaultTo(null)
        .value();
    }, // lastTrial()

    lastTaskIndex() {
      if (this.lastTrial === null) {
        return -1;
      }
      return findIndex(this.sortedTargets, { id: get(this.lastTrial, 'target') });
    }, // lastTrial()

    nextTask() {
      if (this.lastTrial === null) {
        return head(this.sortedTargets);
      }
      const lastTaskOrder = chain(this.sortedTargets)
        .find({ id: get(this.lastTrial, 'target') })
        .get('order', 0)
        .value();

      return chain(this.sortedTargets)
        .filter((target) => !['waiting', 'closed'].includes(target.status) && (target.order > lastTaskOrder))
        .head()
        .defaultTo(head(this.sortedTargets))
        .value();
    }, // nextTask()
  },

  watch: {
    /**
     * Make sure that cache data is synced with cloud data.
     */
    cachedCurrentProgramTrial: {
      immediate: true,
      handler(val) {
        this.cache.currentTrialNumber = val;
      },
    },
    lastTrial: {
      immediate: true,
      handler(val) {
        if (val) {
          this.cache.lastTargetTrialNumber = val.trial;
        }
      },
    },
    lastTaskIndex: {
      immediate: true,
      handler(val) {
        this.cache.lastTaskIndex = val;
      },
    },
  },

  methods: {
    finishSession() {
      if (this.defaultPromptLevel && (this.cache.lastTaskIndex !== -1)) {
        // Record default data for end parts of the previous chain
        const finalData = chain(this.sortedTargets)
          .filter((preliminaryTarget, index) => (
            (index > this.cache.lastTaskIndex)
          ))
          .map((preliminaryTarget) => (
            {
              target: preliminaryTarget.id,
              promptLevel: this.defaultPromptLevel.id,
              scoreValue: this.defaultPromptLevel.score,
              trial: this.currentTrialNumber,
            }))
          .value();

        if (!isEmpty(finalData)) {
          return { finalData };
        }
      }
    }, // finishSession()

    onRecordData(sessionProgram, target, data) {
      // Set defaults
      let { currentTrialNumber } = this;

      const currentTargetIndex = findIndex(this.sortedTargets, target);
      if (currentTargetIndex <= this.cache.lastTaskIndex) {
        if (this.defaultPromptLevel) {
          // Record default data for end parts of the previous chain
          const skippedTrials = chain(this.sortedTargets)
            .filter((preliminaryTarget, index) => (
              (index > this.cache.lastTaskIndex)
            ))
            .map((preliminaryTarget) => ({
              target: preliminaryTarget.id,
              promptLevel: this.defaultPromptLevel.id,
              scoreValue: this.defaultPromptLevel.score,
              trial: currentTrialNumber,
            }))
            .value();
          this.$emit('record-multiple-data', this.sessionProgram, skippedTrials);
        }
        // Increase trial and reset last task to 0
        currentTrialNumber = this.nextTrialNumber;
        this.cache.lastTaskIndex = -1;
      }

      if (this.defaultPromptLevel && (currentTargetIndex > this.cache.lastTaskIndex + 1)) {
        // Record default data for missing parts in the middle of the chain (same trial)
        chain(this.sortedTargets)
          .filter((preliminaryTarget, index) => (
            (index > this.cache.lastTaskIndex)
            && (index < currentTargetIndex)
          ))
          .forEach((preliminaryTarget) => {
            const saveIntermediaryData = {
              promptLevel: this.defaultPromptLevel.id,
              scoreValue: this.defaultPromptLevel.score,
              trial: currentTrialNumber,
            };
            this.$emit('record', sessionProgram, preliminaryTarget, saveIntermediaryData);
          })
          .value();
      }

      const saveData = { ...data, trial: currentTrialNumber };
      this.$emit('record', sessionProgram, target, saveData);

      this.cache.lastTaskIndex = currentTargetIndex;
      this.cache.lastTargetTrialNumber = currentTrialNumber;
      if (currentTrialNumber > this.cache.currentTrialNumber) {
        this.cache.currentTrialNumber = currentTrialNumber;
      }
    }, // onRecordData()

  },
};
</script>
